import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, Star, Trophy, Target, Zap, Edit2, X, User as UserIcon, Loader2, CheckCircle2, AlertCircle, Award, ChevronRight, LogIn, LogOut, Clock, Check, Lock, Crown } from 'lucide-react';
import { UserData } from '../hooks/useUser';
import { cn } from '../lib/utils';
import GameModal, { ModalType } from './GameModal';
import { useMissions } from '../hooks/useMissions';
import { MISSIONS } from '../utils/missions';
import LevelMedal from './LevelMedal';

import { CloudIcon, ShellIcon, StarIcon, GoldRushIcon } from './ModeIcons';
import GalleryPhoto from './GalleryPhoto';
import AvatarFrame from './AvatarFrame';
import { AVATARS } from '../utils/avatars';
import { useTranslation } from 'react-i18next';

interface ProfileProps {
  user: UserData | null;
  onBack: () => void;
  onUpdateNickname: (nickname: string) => Promise<{ success: boolean; error?: string }>;
  onLogout: () => Promise<void>;
  onEquipAvatar: (id: string) => Promise<void>;
  onSignIn: () => Promise<void>;
}

type AchievTab = 'daily' | 'monthly' | 'lifetime';

export default function Profile({ 
  user, onBack, onUpdateNickname, onSignIn, onLogout, onEquipAvatar
}: ProfileProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [newNickname, setNewNickname] = useState(user?.nickname || '');
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'checking' | 'saving' | 'success' | 'error'>('idle');
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [showAchievements, setShowAchievements] = useState(false);
  const [achievTab, setAchievTab] = useState<AchievTab>('daily');
  const [authLoading, setAuthLoading] = useState(false);
  const [showIdentitySelector, setShowIdentitySelector] = useState(false);
  const [selectedAvatarId, setSelectedAvatarId] = useState(user?.avatarId || 'g1');

  // Pull live mission data for the achievements panel
  const { missions } = useMissions(user);

  const [modal, setModal] = useState<{ open: boolean; title: string; message: string; type: ModalType }>({
    open: false, title: '', message: '', type: 'info'
  });

  if (!user) return null;

  const expToNext = user.level * 1000;
  const expProgress = (user.exp / expToNext) * 100;
  const isGuest = !!(user as any).isAnonymous || !!(user.nickname && user.nickname.toLowerCase().startsWith('guest'));

  const gameStats = [
    { label: t('modes.starburst.name'), value: user?.highScores?.starburst ?? 0, Icon: StarIcon, color: 'text-white', bg: 'bg-slate-900', border: 'border-black' },
    { label: t('modes.pemdas.name'), value: user?.highScores?.pemdas ?? 0, Icon: CloudIcon, color: 'text-white', bg: 'bg-[#0ea5e9]', border: 'border-[#0369a1]' },
    { label: t('modes.goldrush.name'), value: user?.highScores?.goldrush ?? 0, Icon: GoldRushIcon, color: 'text-white', bg: 'bg-slate-800', border: 'border-slate-900' },
    { label: t('modes.shellcrush.name'), value: user?.highScores?.shellcrush ?? 0, Icon: ShellIcon, color: 'text-slate-900', bg: 'bg-[#fde68a]', border: 'border-[#d97706]' },
  ];

  const handleUpdateNickname = async () => {
    if (!newNickname.trim() || newNickname === (user?.nickname || user?.displayName) || updateStatus !== 'idle') return;
    setUpdateStatus('checking');
    setNicknameError(null);
    try {
      const result = await onUpdateNickname(newNickname.trim());
      if (result.success) {
        setUpdateStatus('success');
        setTimeout(() => { setIsEditing(false); setUpdateStatus('idle'); }, 1500);
      } else {
        setUpdateStatus('error');
        const errorMsg =
          result.error === 'NAME_TAKEN' ? t('profile.edit_nickname.error_taken') :
          result.error === 'TIMEOUT' ? t('profile.edit_nickname.error_timeout') :
          t('profile.edit_nickname.error_generic');
        setNicknameError(errorMsg);
        setTimeout(() => setUpdateStatus('idle'), 2000);
      }
    } catch {
      setUpdateStatus('error');
      setNicknameError(t('profile.edit_nickname.error_connection'));
      setTimeout(() => setUpdateStatus('idle'), 2000);
    }
  };

  const tabMissions = MISSIONS.filter(m => m.type === achievTab);
  const totalCompleted = MISSIONS.filter(def => {
    const um = missions.find(m => m.missionId === def.id);
    return um && um.isClaimed;
  }).length;

  const achievTabs: { id: AchievTab; label: string; Icon: any }[] = [
    { id: 'daily',    label: 'Daily',    Icon: Clock },
    { id: 'monthly',   label: 'Monthly',   Icon: Target },
    { id: 'lifetime', label: 'Lifetime', Icon: Star },
  ];

  return (
    <div className="flex flex-col h-full bg-[#f8fafc] text-slate-900 p-6 relative overflow-hidden">

      <div className="absolute top-0 right-0 w-64 h-64 bg-slate-200/40 rounded-full blur-3xl -mr-32 -mt-32" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-sky-100/40 rounded-full blur-3xl -ml-32 -mb-32" />

      {/* Header */}
      <div className="flex items-center gap-4 mb-8 relative z-10">
        <button onClick={onBack} className="p-3 bg-white rounded-2xl toy-3d-button border-none">
          <ArrowLeft className="w-6 h-6 text-slate-600" />
        </button>
        <h1 className="text-3xl font-num font-black tracking-tight text-slate-900 uppercase">{t('profile.title')}</h1>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar relative z-10 space-y-6 pb-12">

        {/* ── Profile Card ── */}
        <div className="bg-white rounded-[40px] p-8 border-none relative overflow-hidden shadow-md border-b-[12px] border-slate-200">
          {/* Style Details */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '4px 100%' }} />
          <div className="absolute inset-0 bg-white/40" style={{ clipPath: 'polygon(0 0, 100% 0, 50% 50%)' }} />

          <div className="flex flex-col items-center relative z-10">
            <button 
              onClick={() => {
                setSelectedAvatarId(user.avatarId || 'g1');
                setShowIdentitySelector(true);
              }}
              className="flex items-center justify-center mb-6 relative group transition-all active:scale-95"
            >
              <AvatarFrame size="xl">
                <GalleryPhoto id={user.avatarId || 'g1'} size="xl" className="w-full h-full" />
              </AvatarFrame>
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl pointer-events-none">
                <p className="text-[8px] font-black text-white uppercase tracking-[0.2em]">{t('profile.customize')}</p>
              </div>
            </button>

            <div className="flex items-center gap-2 mb-2 w-full justify-center">
              <h2 
                className="font-num tracking-tight text-slate-900 uppercase text-center"
                style={{ 
                  fontSize: (user.nickname || user.displayName || t('common.player_fallback')).length > 12 ? '1.25rem' : 
                            (user.nickname || user.displayName || t('common.player_fallback')).length > 8 ? '1.75rem' : '2.25rem',
                  lineHeight: '1.1'
                }}
              >
                {user.nickname || user.displayName || t('common.player_fallback')}
              </h2>
              {!isGuest && (
                <button
                  onClick={() => { setNewNickname(user.nickname || user.displayName || ''); setIsEditing(true); }}
                  className="p-2 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors border-b-2 border-slate-200 active:translate-y-0.5 active:border-b-0"
                >
                  <Edit2 className="w-4 h-4 text-slate-400" />
                </button>
              )}
            </div>
            {isGuest && <span className="text-[10px] font-black text-amber-500 uppercase tracking-widest mb-1 italic">{t('profile.guest_account')}</span>}
            <div className="flex items-center justify-center gap-2 mt-1 mb-3">
              <div className="flex items-center gap-1.5 bg-[#fbbf24] text-slate-900 text-xs font-black px-3.5 py-1.5 rounded-xl shadow-sm border-b-4 border-amber-600">
                {user.isPremium && <Crown className="w-4 h-4 text-slate-900 fill-slate-900 shrink-0" />}
                <span>LVL {user.level}</span>
              </div>
            </div>
            <p className="text-slate-400 font-black text-xs uppercase tracking-[0.2em] mb-8 opacity-60">{t('profile.uid_label', { id: user.uid.slice(0, 8) })}</p>

            <div className="w-full space-y-3">
              <div className="flex justify-between items-end">
                <span className="text-xs font-black text-slate-500 uppercase tracking-widest opacity-60">{t('profile.growth_progress')}</span>
                <span className="text-sm font-num text-slate-400">{user.exp} / {expToNext}</span>
              </div>
              <div className="h-6 bg-slate-100 rounded-3xl overflow-hidden p-1 shadow-inner relative border-none">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${expProgress}%` }}
                  className="h-full bg-gradient-to-r from-[#0ea5e9] to-[#8b5cf6] rounded-full relative overflow-hidden"
                >
                   <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '4px 100%' }} />
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Personal Record Section ── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 px-2">
            <Trophy className="w-4 h-4 text-[#fbbf24]" />
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">{t('profile.personal_record')}</h3>
          </div>
          
          <div className="grid grid-cols-1 gap-3">
            {gameStats.map((stat, i) => (
              <div
                key={i}
                className="bg-white p-4 rounded-[28px] border-none flex items-center gap-4 shadow-sm border-b-4 border-slate-200 relative overflow-hidden active:translate-y-0.5 transition-all group"
              >
                <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'linear-gradient(45deg, #000 1px, transparent 1px)', backgroundSize: '4px 4px' }} />
                
                <div className={cn(
                  'p-3 rounded-2xl relative overflow-hidden border-b-2 transition-all shadow-inner shrink-0', 
                  stat.bg, 
                  stat.border
                )}>
                  <stat.Icon className={cn('w-6 h-6 relative z-10 drop-shadow-md', stat.color)} />
                </div>
                
                <div className="flex-1">
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest opacity-60 mb-1">{stat.label}</div>
                  <div className="text-2xl font-num font-black text-slate-900 leading-none">{typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</div>
                </div>

                <ChevronRight className="w-5 h-5 text-slate-200 mr-2" />
              </div>
            ))}
          </div>
        </div>


        
        {/* ── Sign In ── */}
        <div className="pt-2 pb-6 relative z-10">
          {isGuest && (
            <button
              onClick={async () => {
                setAuthLoading(true);
                try { 
                  await onSignIn(); 
                } catch (err: any) {
                  console.error("Sign-in error:", err);
                  window.alert("Sign-In Failed: " + (err.message || "Unknown error"));
                } finally { 
                  setAuthLoading(false); 
                }
              }}
              disabled={authLoading}
              className="w-full bg-[#0ea5e9] text-white font-num text-lg py-5 rounded-[32px] toy-3d-button border-none flex items-center justify-center gap-3 transition-all disabled:opacity-60"
            >
              {authLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}
              {authLoading ? t('profile.connecting') : t('profile.sign_in')}
            </button>
          )}
        </div>
      </div>



      {/* ── Edit Nickname Modal ── */}
      <AnimatePresence>
        {isEditing && (
          <div className="absolute inset-0 z-[120] flex items-center justify-center p-6 text-slate-900">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setIsEditing(false)}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white w-full max-w-sm rounded-[40px] p-8 toy-3d-button border-none relative z-10 shadow-2xl"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-num tracking-tight uppercase">{t('profile.edit_nickname.title')}</h3>
                <button onClick={() => setIsEditing(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="mb-8 text-slate-900">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">{t('profile.edit_nickname.label')}</label>
                <div className="relative">
                  <input
                    type="text"
                    value={newNickname}
                    onChange={(e) => setNewNickname(e.target.value)}
                    placeholder={t('profile.edit_nickname.placeholder')}
                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl p-4 pl-12 text-sm font-black text-slate-900 focus:outline-none focus:border-[#0ea5e9] transition-all placeholder:text-slate-300"
                  />
                  <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                </div>
                {nicknameError && (
                  <p className="mt-2 text-[#f43f5e] text-[10px] font-black uppercase tracking-widest text-center">{nicknameError}</p>
                )}

                {/* Nickname Rules */}
                <div className="mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('profile.edit_nickname.rules_header')}</p>
                  <ul className="space-y-1">
                    <li className="flex items-center gap-2 text-[10px] text-slate-500 font-bold">
                      <div className="w-1 h-1 rounded-full bg-[#0ea5e9]" />
                      {t('profile.edit_nickname.rule_length')}
                    </li>
                    <li className="flex items-center gap-2 text-[10px] text-slate-500 font-bold">
                      <div className="w-1 h-1 rounded-full bg-[#0ea5e9]" />
                      {t('profile.edit_nickname.rule_chars')}
                    </li>
                    <li className="flex items-center gap-2 text-[10px] text-slate-500 font-bold">
                      <div className="w-1 h-1 rounded-full bg-rose-400" />
                      {t('profile.edit_nickname.rule_guest')}
                    </li>
                    <li className="flex items-center gap-2 text-[10px] text-slate-500 font-bold">
                      <div className="w-1 h-1 rounded-full bg-rose-400" />
                      {t('profile.edit_nickname.rule_language')}
                    </li>
                  </ul>
                </div>
              </div>

              <button
                onClick={handleUpdateNickname}
                disabled={updateStatus !== 'idle' || !newNickname.trim() || newNickname === user?.nickname}
                className={cn(
                  'w-full font-num text-2xl py-5 rounded-3xl toy-3d-button border-none transition-all relative overflow-hidden',
                  updateStatus === 'idle' && 'bg-[#0ea5e9] text-white',
                  updateStatus === 'checking' && 'bg-cyan-100 text-[#0ea5e9] cursor-wait',
                  updateStatus === 'success' && 'bg-green-500 text-white',
                  updateStatus === 'error' && 'bg-[#f43f5e] text-white',
                )}
              >
                <motion.div
                  animate={updateStatus === 'error' ? { x: [-4, 4, -4, 4, 0] } : {}}
                  transition={{ duration: 0.4 }}
                  className="flex items-center justify-center gap-2"
                >
                  {updateStatus === 'checking' && <Loader2 className="w-6 h-6 animate-spin" />}
                  {updateStatus === 'success' && <CheckCircle2 className="w-6 h-6" />}
                  {updateStatus === 'error' && <AlertCircle className="w-6 h-6" />}
                  <span>
                    {updateStatus === 'idle' && t('profile.edit_nickname.save')}
                    {updateStatus === 'checking' && t('profile.edit_nickname.verifying')}
                    {updateStatus === 'success' && t('profile.edit_nickname.updated')}
                    {updateStatus === 'error' && t('profile.edit_nickname.declined')}
                  </span>
                </motion.div>
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Identity Customizer Modal ── */}
      <AnimatePresence>
        {showIdentitySelector && (
          <div className="absolute inset-0 z-[2000] flex items-end justify-center sm:items-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setSelectedAvatarId(user.avatarId || 'g1');
                setShowIdentitySelector(false);
              }}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
            />
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="bg-white w-full max-w-md h-[85vh] rounded-t-[40px] sm:rounded-[40px] flex flex-col relative z-10 overflow-hidden"
            >
              {/* Header */}
              <div className="px-6 pt-8 pb-4 border-b border-slate-100 shrink-0">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-3xl font-num uppercase tracking-tight text-slate-900 leading-none">{t('profile.identity.title')}</h2>
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mt-2">{t('profile.identity.subtitle')}</p>
                  </div>
                  <button onClick={() => {
                    setSelectedAvatarId(user.avatarId || 'g1');
                    setShowIdentitySelector(false);
                  }} className="p-2 hover:bg-slate-50 rounded-full transition-colors">
                    <X className="w-6 h-6 text-slate-400" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 no-scrollbar">
                <div className="space-y-6">
                  <div className="bg-slate-900 rounded-[32px] p-8 text-center relative overflow-hidden">
                      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(45deg, #fff 1px, transparent 1px)', backgroundSize: '10px 10px' }} />
                      <p className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.3em] mb-6 relative z-10">{t('profile.identity.current')}</p>
                      <div className="flex justify-center relative z-10">
                          <AvatarFrame size="xl">
                            <GalleryPhoto id={selectedAvatarId} size="xl" className="w-full h-full" />
                          </AvatarFrame>
                      </div>
                  </div>

                  <div className="space-y-4">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('profile.identity.select_portrait')}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {AVATARS.filter(a => a.id.startsWith('g') || user.purchasedAvatars?.includes(a.id)).map((avatar) => (
                        <button
                          key={avatar.id}
                          onClick={() => setSelectedAvatarId(avatar.id)}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-2xl transition-all border border-slate-100",
                            selectedAvatarId === avatar.id ? "bg-cyan-50 ring-2 ring-cyan-500" : "bg-white hover:bg-slate-50"
                          )}
                        >
                          <GalleryPhoto id={avatar.id} size="sm" className="shrink-0" />
                          <div className="min-w-0 text-left">
                              <div className="text-[10px] font-black text-slate-900 uppercase tracking-tight truncate">{avatar.name}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-slate-100 bg-slate-50 shrink-0">
                <button
                  onClick={async () => {
                    await onEquipAvatar(selectedAvatarId);
                    setShowIdentitySelector(false);
                  }}
                  className="w-full bg-slate-900 text-white py-5 rounded-[24px] font-num text-lg uppercase tracking-widest shadow-xl active:scale-95 transition-all"
                >
                  {t('profile.identity.confirm')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <GameModal
        isOpen={modal.open}
        onClose={() => setModal({ ...modal, open: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
    </div>
  );
}
